import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Package, 
  Beaker, 
  AlertTriangle, 
  TrendingUp, 
  Droplet,
  Box,
  BarChart3,
  Plus,
  Minus,
  Search,
  RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Production = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('inventory');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addFormulaDialogOpen, setAddFormulaDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedMarket, setSelectedMarket] = useState('all');
  const [selectedReportMarket, setSelectedReportMarket] = useState('all');
  const [markets, setMarkets] = useState([]);
  const [editFormData, setEditFormData] = useState({
    name: '',
    sku: '',
    quantity: 0,
    unit: '',
    minStock: 0,
    category: '',
    costPerUnit: 0,
    lastRestocked: '',
    market: '',
  });
  const [addFormData, setAddFormData] = useState({
    name: '',
    sku: '',
    quantity: 0,
    unit: 'gallons',
    minStock: 0,
    category: 'Base Coatings',
    costPerUnit: 0,
    lastRestocked: new Date().toISOString().split('T')[0],
    market: 'Jacksonville',
  });

  // Inventory State - now loaded from API
  const [inventory, setInventory] = useState([]);

  // Coating Mix Formulas
  const [mixFormulas, setMixFormulas] = useState([
    { id: 1, name: 'RhinoShield Classic White', code: 'RS-WHT-001', baseCoating: 10, pigment: 0, primer: 2, sealer: 1, description: 'Standard white exterior coating' },
    { id: 2, name: 'RhinoShield Desert Sand', code: 'RS-DST-002', baseCoating: 10, pigment: 1.5, primer: 2, sealer: 1, description: 'Popular southwest color' },
    { id: 3, name: 'RhinoShield Ocean Blue', code: 'RS-BLU-003', baseCoating: 10, pigment: 2, primer: 2, sealer: 1, description: 'Coastal blue finish' },
    { id: 4, name: 'RhinoShield Custom Beige', code: 'RS-BGE-004', baseCoating: 10, pigment: 1, primer: 2, sealer: 1, description: 'Neutral beige tone' },
  ]);

  const [addFormulaData, setAddFormulaData] = useState({
    name: '',
    code: '',
    baseCoating: 10,
    pigment: 0,
    primer: 2,
    sealer: 1,
    description: '',
  });

  // Filter inventory by selected market (for Inventory tab)
  const getFilteredInventory = () => {
    if (selectedMarket === 'all') {
      return inventory;
    }
    return inventory.filter(item => item.market === selectedMarket);
  };

  // Filter inventory by selected market (for Reports tab)
  const getReportFilteredInventory = () => {
    if (selectedReportMarket === 'all') {
      return inventory;
    }
    return inventory.filter(item => item.market === selectedReportMarket);
  };

  // Calculate inventory metrics
  const calculateInventoryMetrics = () => {
    const filteredInventory = getFilteredInventory();
    const totalValue = filteredInventory.reduce((sum, item) => sum + (item.quantity * item.costPerUnit), 0);
    const lowStockItems = filteredInventory.filter(item => item.quantity < item.minStock).length;
    const highValueItems = filteredInventory.filter(item => (item.quantity * item.costPerUnit) > 10000).length;
    
    return {
      totalInventoryValue: totalValue,
      lowStockItems,
      highValueItems,
      totalItems: filteredInventory.length,
    };
  };

  // Get unique markets from inventory
  const getInventoryMarkets = () => {
    return [...new Set(inventory.map(item => item.market))].sort();
  };

  // Production Metrics
  const [metrics, setMetrics] = useState({
    batchesProducedToday: 12,
    batchesProducedWeek: 78,
    totalGallonsMixed: 1560,
    averageMixTime: 45,
    qualityPassRate: 98.5,
    lowStockItems: 1,
  });

  const inventoryMetrics = calculateInventoryMetrics();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch markets
        const marketsResponse = await axios.get(`${API}/markets`);
        setMarkets(marketsResponse.data);

        // Fetch inventory
        const inventoryResponse = await axios.get(`${API}/inventory`);
        
        // Map backend data to frontend format
        const mappedInventory = inventoryResponse.data.map(item => ({
          id: item.id,
          name: item.name,
          sku: item.id.substring(0, 12).toUpperCase(), // Generate SKU from ID
          quantity: item.quantity,
          unit: item.unit,
          minStock: item.low_stock_threshold || 0,
          category: item.category,
          lastRestocked: item.updated_at ? item.updated_at.split('T')[0] : new Date().toISOString().split('T')[0],
          costPerUnit: item.cost_per_unit,
          market: marketsResponse.data.find(m => m.id === item.market_id)?.name || 'Unknown',
          market_id: item.market_id,
        }));
        
        setInventory(mappedInventory);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getStockStatus = (quantity, minStock) => {
    const percentage = (quantity / minStock) * 100;
    if (percentage < 50) return { status: 'critical', color: 'bg-red-500', label: 'Critical' };
    if (percentage < 100) return { status: 'low', color: 'bg-orange-500', label: 'Low' };
    return { status: 'good', color: 'bg-green-500', label: 'Good' };
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleItemClick = (item) => {
    setSelectedItem(item);
    setEditFormData({
      name: item.name,
      sku: item.sku,
      quantity: item.quantity,
      unit: item.unit,
      minStock: item.minStock,
      category: item.category,
      costPerUnit: item.costPerUnit,
      lastRestocked: item.lastRestocked,
      market: item.market,
    });
    setEditDialogOpen(true);
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData({
      ...editFormData,
      [field]: value,
    });
  };

  const handleSaveItem = async () => {
    try {
      // Find the market_id from the selected market name
      const market = markets.find(m => m.name === editFormData.market);
      if (!market) {
        toast.error('Invalid market selected');
        return;
      }

      // Prepare update data in backend format
      const updateData = {
        name: editFormData.name,
        category: editFormData.category,
        unit: editFormData.unit,
        quantity: parseFloat(editFormData.quantity),
        cost_per_unit: parseFloat(editFormData.costPerUnit),
        market_id: market.id,
        low_stock_threshold: parseFloat(editFormData.minStock),
        notes: null,
      };

      // Update via API
      const response = await axios.put(
        `${API}/inventory/${selectedItem.id}`,
        updateData
      );

      // Update the item in local state
      const updatedInventory = inventory.map(item => 
        item.id === selectedItem.id 
          ? {
              ...item,
              ...editFormData,
              market_id: market.id,
            }
          : item
      );
      setInventory(updatedInventory);
      setEditDialogOpen(false);
      toast.success('Item updated successfully');
    } catch (error) {
      console.error('Error updating item:', error);
      toast.error('Failed to update item');
    }
  };

  const handleDeleteItem = async () => {
    if (window.confirm(`Are you sure you want to delete "${selectedItem.name}"?`)) {
      try {
        // Delete via API
        await axios.delete(`${API}/inventory/${selectedItem.id}`);

        // Remove from local state
        const updatedInventory = inventory.filter(item => item.id !== selectedItem.id);
        setInventory(updatedInventory);
        setEditDialogOpen(false);
        toast.success('Item deleted successfully');
      } catch (error) {
        console.error('Error deleting item:', error);
        toast.error('Failed to delete item');
      }
    }
  };

  const handleOpenAddDialog = () => {
    setAddFormData({
      name: '',
      sku: '',
      quantity: 0,
      unit: 'gallons',
      minStock: 0,
      category: 'Base Coatings',
      costPerUnit: 0,
      lastRestocked: new Date().toISOString().split('T')[0],
      market: 'Jacksonville',
    });
    setAddDialogOpen(true);
  };

  const handleAddFormChange = (field, value) => {
    setAddFormData({
      ...addFormData,
      [field]: value,
    });
  };

  const handleAddItem = async () => {
    try {
      // Find the market_id from the selected market name
      const market = markets.find(m => m.name === addFormData.market);
      if (!market) {
        toast.error('Invalid market selected');
        return;
      }

      // Prepare data in backend format
      const itemData = {
        name: addFormData.name,
        category: addFormData.category,
        unit: addFormData.unit,
        quantity: parseFloat(addFormData.quantity),
        cost_per_unit: parseFloat(addFormData.costPerUnit),
        market_id: market.id,
        low_stock_threshold: parseFloat(addFormData.minStock),
        notes: null,
      };

      // Create via API
      const response = await axios.post(
        `${API}/inventory`,
        itemData
      );

      // Add to local state
      const newItem = {
        id: response.data.id,
        ...addFormData,
        sku: response.data.id.substring(0, 12).toUpperCase(),
        market_id: market.id,
      };
      
      setInventory([...inventory, newItem]);
      setAddDialogOpen(false);
      toast.success('Item added successfully');
    } catch (error) {
      console.error('Error adding item:', error);
      toast.error('Failed to add item');
    }
  };

  const handleOpenAddFormulaDialog = () => {
    setAddFormulaData({
      name: '',
      code: '',
      baseCoating: 10,
      pigment: 0,
      primer: 2,
      sealer: 1,
      description: '',
    });
    setAddFormulaDialogOpen(true);
  };

  const handleAddFormulaChange = (field, value) => {
    setAddFormulaData({
      ...addFormulaData,
      [field]: value,
    });
  };

  const handleAddFormula = () => {
    // Generate new ID
    const newId = Math.max(...mixFormulas.map(f => f.id)) + 1;
    
    // Create new formula
    const newFormula = {
      id: newId,
      ...addFormulaData,
    };
    
    // Add to formulas
    setMixFormulas([...mixFormulas, newFormula]);
    setAddFormulaDialogOpen(false);
    toast.success('Formula added successfully');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading production data...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Production Center</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage inventory, coating formulas, and production metrics</p>
        </div>

        {/* Production Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Inventory Value</CardTitle>
              <Package className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${inventoryMetrics.totalInventoryValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{inventoryMetrics.totalItems} items in stock</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Batches This Week</CardTitle>
              <Beaker className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.batchesProducedWeek}</div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{metrics.batchesProducedToday} today</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Quality Rate</CardTitle>
              <BarChart3 className="h-4 w-4 text-cyan-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.qualityPassRate}%</div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Pass rate</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Stock Alerts</CardTitle>
              <AlertTriangle className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{inventoryMetrics.lowStockItems}</div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Low stock items</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for Inventory, Mixing, Reports */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 lg:w-[600px]">
            <TabsTrigger value="inventory">
              <Package className="h-4 w-4 mr-2" />
              Inventory
            </TabsTrigger>
            <TabsTrigger value="mixing">
              <Beaker className="h-4 w-4 mr-2" />
              Coating Formulas
            </TabsTrigger>
            <TabsTrigger value="reports">
              <BarChart3 className="h-4 w-4 mr-2" />
              Production Reports
            </TabsTrigger>
          </TabsList>

          {/* Inventory Tab */}
          <TabsContent value="inventory" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Inventory Management</CardTitle>
                    <CardDescription>Track coating materials, supplies, and stock levels by market</CardDescription>
                  </div>
                  <div className="flex items-center gap-3">
                    <Select value={selectedMarket} onValueChange={setSelectedMarket}>
                      <SelectTrigger className="w-[200px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">All Markets</span>
                          </div>
                        </SelectItem>
                        {getInventoryMarkets().map((market) => (
                          <SelectItem key={market} value={market}>
                            {market}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button onClick={handleOpenAddDialog}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Item
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Search Bar */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input placeholder="Search inventory..." className="pl-10" />
                  </div>
                  <Button variant="outline">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                </div>

                {/* Inventory Table */}
                <div className="space-y-4">
                  {getFilteredInventory().length === 0 ? (
                    <div className="text-center py-12">
                      <Package className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600 dark:text-gray-400">No inventory items found for this market</p>
                    </div>
                  ) : (
                    getFilteredInventory().map((item) => {
                    const stockStatus = getStockStatus(item.quantity, item.minStock);
                    const totalValue = item.quantity * item.costPerUnit;
                    return (
                      <div
                        key={item.id}
                        onClick={() => handleItemClick(item)}
                        className="border border-border rounded-lg p-4 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors cursor-pointer hover:shadow-md group"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <Box className="h-5 w-5 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
                              <div>
                                <h3 className="font-semibold text-gray-900 dark:text-white group-hover:text-blue-900 dark:group-hover:text-blue-100">{item.name}</h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400 group-hover:text-blue-700 dark:group-hover:text-blue-300">SKU: {item.sku}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 text-sm mt-3">
                              <div>
                                <span className="text-gray-600 dark:text-gray-400">Market: </span>
                                <Badge variant="outline" className="font-medium">{item.market}</Badge>
                              </div>
                              <div>
                                <span className="text-gray-600 dark:text-gray-400">Category: </span>
                                <Badge variant="secondary">{item.category}</Badge>
                              </div>
                              <div>
                                <span className="text-gray-600 dark:text-gray-400">Cost/Unit: </span>
                                <span className="font-medium text-green-600 dark:text-green-400">
                                  ${item.costPerUnit.toFixed(2)}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-600 dark:text-gray-400">Total Value: </span>
                                <span className="font-bold text-blue-600 dark:text-blue-400">
                                  ${totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-600 dark:text-gray-400">Last Restocked: </span>
                                <span className="font-medium">{formatDate(item.lastRestocked)}</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="text-right min-w-[140px]">
                            <div className="flex items-center justify-end gap-2 mb-2">
                              <div className={`w-2 h-2 rounded-full ${stockStatus.color}`}></div>
                              <Badge variant={stockStatus.status === 'critical' ? 'destructive' : 'secondary'}>
                                {stockStatus.label}
                              </Badge>
                            </div>
                            <div className="text-2xl font-bold text-gray-900 dark:text-white">
                              {item.quantity}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              {item.unit} (Min: {item.minStock})
                            </div>
                            <div className="flex items-center justify-end gap-2 mt-3">
                              <Button size="sm" variant="outline">
                                <Minus className="h-3 w-3" />
                              </Button>
                              <Button size="sm" variant="outline">
                                <Plus className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Coating Formulas Tab */}
          <TabsContent value="mixing" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Coating Mix Formulas</CardTitle>
                    <CardDescription>RhinoShield coating formulations and mixing ratios</CardDescription>
                  </div>
                  <Button onClick={handleOpenAddFormulaDialog}>
                    <Plus className="h-4 w-4 mr-2" />
                    New Formula
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {mixFormulas.map((formula) => (
                    <Card key={formula.id} className="border-2">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="text-lg">{formula.name}</CardTitle>
                            <CardDescription className="mt-1">{formula.description}</CardDescription>
                          </div>
                          <Badge variant="outline" className="font-mono text-xs">
                            {formula.code}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                            Mixing Ratios (per batch):
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-950/30 rounded">
                              <span className="text-sm text-gray-600 dark:text-gray-400">Base Coating</span>
                              <span className="font-bold">{formula.baseCoating} gal</span>
                            </div>
                            <div className="flex items-center justify-between p-2 bg-purple-50 dark:bg-purple-950/30 rounded">
                              <span className="text-sm text-gray-600 dark:text-gray-400">Pigment</span>
                              <span className="font-bold">{formula.pigment} gal</span>
                            </div>
                            <div className="flex items-center justify-between p-2 bg-green-50 dark:bg-green-950/30 rounded">
                              <span className="text-sm text-gray-600 dark:text-gray-400">Primer</span>
                              <span className="font-bold">{formula.primer} gal</span>
                            </div>
                            <div className="flex items-center justify-between p-2 bg-orange-50 dark:bg-orange-950/30 rounded">
                              <span className="text-sm text-gray-600 dark:text-gray-400">Sealer</span>
                              <span className="font-bold">{formula.sealer} gal</span>
                            </div>
                          </div>
                          <div className="pt-3 border-t">
                            <div className="flex items-center justify-between text-sm font-medium">
                              <span>Total Yield:</span>
                              <span className="text-lg">
                                {formula.baseCoating + formula.pigment + formula.primer + formula.sealer} gallons
                              </span>
                            </div>
                          </div>
                          <Button className="w-full mt-3" variant="outline">
                            <Beaker className="h-4 w-4 mr-2" />
                            Mix Batch
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Production Reports Tab */}
          <TabsContent value="reports" className="space-y-4">
            {/* Market Filter for Reports */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Production Reports</h3>
                <p className="text-sm text-muted-foreground">
                  {selectedReportMarket === 'all' 
                    ? 'Viewing total company analytics across all markets' 
                    : `Viewing ${selectedReportMarket} market analytics`}
                </p>
              </div>
              <Select value={selectedReportMarket} onValueChange={setSelectedReportMarket}>
                <SelectTrigger className="w-[220px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Total Company</span>
                    </div>
                  </SelectItem>
                  {getInventoryMarkets().map((market) => (
                    <SelectItem key={market} value={market}>
                      {market}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Cost Analytics Section */}
            <Card className="border-2 border-green-500/20 bg-green-50/50 dark:bg-green-950/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                  Inventory Cost Analytics
                </CardTitle>
                <CardDescription>
                  {selectedReportMarket === 'all' 
                    ? 'Real-time cost tracking across all markets' 
                    : `${selectedReportMarket} market cost analysis`}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {(() => {
                  const reportInv = getReportFilteredInventory();
                  const reportTotalValue = reportInv.reduce((sum, item) => sum + (item.quantity * item.costPerUnit), 0);
                  const reportHighValue = reportInv.filter(item => (item.quantity * item.costPerUnit) > 10000).length;
                  const reportLowStockValue = reportInv
                    .filter(item => item.quantity < item.minStock)
                    .reduce((sum, item) => sum + (item.quantity * item.costPerUnit), 0);
                  
                  return (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border-2 border-green-500/30">
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Inventory Value</p>
                        <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                          ${reportTotalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      </div>
                      <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border-2 border-blue-500/30">
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Avg Cost Per Item</p>
                        <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                          ${reportInv.length > 0 ? (reportTotalValue / reportInv.length).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                        </p>
                      </div>
                      <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border-2 border-purple-500/30">
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">High-Value Items</p>
                        <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                          {reportHighValue}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Value &gt; $10,000</p>
                      </div>
                      <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border-2 border-orange-500/30">
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Low Stock Value</p>
                        <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                          ${reportLowStockValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Inventory by Market */}
              <Card>
                <CardHeader>
                  <CardTitle>Inventory Value by Market</CardTitle>
                  <CardDescription>Total inventory value per market location</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {getInventoryMarkets().map((market) => {
                      const marketItems = inventory.filter(item => item.market === market);
                      const marketValue = marketItems.reduce((sum, item) => sum + (item.quantity * item.costPerUnit), 0);
                      const marketQuantity = marketItems.reduce((sum, item) => sum + item.quantity, 0);
                      const totalValue = inventory.reduce((sum, item) => sum + (item.quantity * item.costPerUnit), 0);
                      const percentage = (marketValue / totalValue) * 100;
                      
                      return (
                        <div key={market} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{market}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {marketItems.length} items • {marketQuantity} units
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-gray-900 dark:text-white">
                                ${marketValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{percentage.toFixed(1)}%</p>
                            </div>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div 
                              className="bg-green-600 h-2 rounded-full" 
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Cost by Category */}
              <Card>
                <CardHeader>
                  <CardTitle>Inventory Value by Category</CardTitle>
                  <CardDescription>
                    {selectedReportMarket === 'all' ? 'Total company' : `${selectedReportMarket} market only`}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {[...new Set(getReportFilteredInventory().map(item => item.category))].map((category) => {
                      const filteredInv = getReportFilteredInventory();
                      const categoryItems = filteredInv.filter(item => item.category === category);
                      const categoryValue = categoryItems.reduce((sum, item) => sum + (item.quantity * item.costPerUnit), 0);
                      const categoryQuantity = categoryItems.reduce((sum, item) => sum + item.quantity, 0);
                      const totalValue = filteredInv.reduce((sum, item) => sum + (item.quantity * item.costPerUnit), 0);
                      const percentage = totalValue > 0 ? (categoryValue / totalValue) * 100 : 0;
                      
                      return (
                        <div key={category} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{category}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {categoryItems.length} items • {categoryQuantity} units
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-gray-900 dark:text-white">
                                ${categoryValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{percentage.toFixed(1)}%</p>
                            </div>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full" 
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Top Value Items */}
              <Card>
                <CardHeader>
                  <CardTitle>Highest Value Items</CardTitle>
                  <CardDescription>
                    {selectedReportMarket === 'all' ? 'Total company' : `${selectedReportMarket} market only`}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {[...getReportFilteredInventory()]
                      .sort((a, b) => (b.quantity * b.costPerUnit) - (a.quantity * a.costPerUnit))
                      .slice(0, 5)
                      .map((item, index) => {
                        const totalValue = item.quantity * item.costPerUnit;
                        return (
                          <div key={item.id} className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-accent transition-colors">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-green-600 text-white flex items-center justify-center font-bold text-sm">
                                {index + 1}
                              </div>
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">{item.name}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {item.quantity} {item.unit} × ${item.costPerUnit.toFixed(2)}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-green-600 dark:text-green-400">
                                ${totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </p>
                              <Badge variant="secondary" className="text-xs mt-1">{item.category}</Badge>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </CardContent>
              </Card>

              {/* Weekly Production Summary */}
              <Card>
                <CardHeader>
                  <CardTitle>Weekly Production Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Total Batches</p>
                        <p className="text-2xl font-bold">{metrics.batchesProducedWeek}</p>
                      </div>
                      <TrendingUp className="h-8 w-8 text-blue-600" />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-950/30 rounded-lg">
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Avg Mix Time</p>
                        <p className="text-2xl font-bold">{metrics.averageMixTime} min</p>
                      </div>
                      <BarChart3 className="h-8 w-8 text-green-600" />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-950/30 rounded-lg">
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Quality Pass Rate</p>
                        <p className="text-2xl font-bold">{metrics.qualityPassRate}%</p>
                      </div>
                      <BarChart3 className="h-8 w-8 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Top Production Items */}
              <Card>
                <CardHeader>
                  <CardTitle>Top Production Items</CardTitle>
                  <CardDescription>Most mixed coatings this week</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {mixFormulas.slice(0, 4).map((formula, index) => (
                      <div key={formula.id} className="flex items-center justify-between p-3 border border-border rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">{formula.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{formula.code}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold">{18 - index * 3} batches</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {(18 - index * 3) * (formula.baseCoating + formula.pigment + formula.primer + formula.sealer)} gal
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Edit Item Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Edit Inventory Item
              </DialogTitle>
              <DialogDescription>
                Update item details, quantities, and cost information
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Item Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Item Name</Label>
                <Input
                  id="name"
                  value={editFormData.name}
                  onChange={(e) => handleEditFormChange('name', e.target.value)}
                  placeholder="e.g., Base Coating - White"
                />
              </div>

              {/* SKU and Market */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sku">SKU</Label>
                  <Input
                    id="sku"
                    value={editFormData.sku}
                    onChange={(e) => handleEditFormChange('sku', e.target.value)}
                    placeholder="e.g., BC-WHT-001"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="market">Market</Label>
                  <Select 
                    value={editFormData.market}
                    onValueChange={(value) => handleEditFormChange('market', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {getInventoryMarkets().map((market) => (
                        <SelectItem key={market} value={market}>
                          {market}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select 
                  value={editFormData.category}
                  onValueChange={(value) => handleEditFormChange('category', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Base Coatings">Base Coatings</SelectItem>
                    <SelectItem value="Pigments">Pigments</SelectItem>
                    <SelectItem value="Primers">Primers</SelectItem>
                    <SelectItem value="Sealers">Sealers</SelectItem>
                    <SelectItem value="Additives">Additives</SelectItem>
                    <SelectItem value="Supplies">Supplies</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Quantity and Unit */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="quantity">Quantity</Label>
                  <Input
                    id="quantity"
                    type="number"
                    value={editFormData.quantity}
                    onChange={(e) => handleEditFormChange('quantity', parseFloat(e.target.value) || 0)}
                    placeholder="450"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="unit">Unit</Label>
                  <Select 
                    value={editFormData.unit}
                    onValueChange={(value) => handleEditFormChange('unit', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gallons">Gallons</SelectItem>
                      <SelectItem value="rolls">Rolls</SelectItem>
                      <SelectItem value="units">Units</SelectItem>
                      <SelectItem value="boxes">Boxes</SelectItem>
                      <SelectItem value="pounds">Pounds</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Min Stock and Cost Per Unit */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="minStock">Minimum Stock</Label>
                  <Input
                    id="minStock"
                    type="number"
                    value={editFormData.minStock}
                    onChange={(e) => handleEditFormChange('minStock', parseFloat(e.target.value) || 0)}
                    placeholder="200"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="costPerUnit">Cost Per Unit ($)</Label>
                  <Input
                    id="costPerUnit"
                    type="number"
                    step="0.01"
                    value={editFormData.costPerUnit}
                    onChange={(e) => handleEditFormChange('costPerUnit', parseFloat(e.target.value) || 0)}
                    placeholder="45.50"
                  />
                </div>
              </div>

              {/* Last Restocked */}
              <div className="space-y-2">
                <Label htmlFor="lastRestocked">Last Restocked</Label>
                <Input
                  id="lastRestocked"
                  type="date"
                  value={editFormData.lastRestocked}
                  onChange={(e) => handleEditFormChange('lastRestocked', e.target.value)}
                />
              </div>

              {/* Calculated Values Display */}
              {selectedItem && (
                <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Current Stock Status</p>
                      <div className="flex items-center gap-2 mt-1">
                        {(() => {
                          const status = getStockStatus(editFormData.quantity, editFormData.minStock);
                          return (
                            <>
                              <div className={`w-2 h-2 rounded-full ${status.color}`}></div>
                              <Badge variant={status.status === 'critical' ? 'destructive' : 'secondary'}>
                                {status.label}
                              </Badge>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Total Value</p>
                      <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">
                        ${(editFormData.quantity * editFormData.costPerUnit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button 
                variant="destructive" 
                onClick={handleDeleteItem}
                className="mr-auto"
              >
                <Minus className="h-4 w-4 mr-2" />
                Delete Item
              </Button>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveItem}>
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Item Dialog */}
        <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5" />
                Add New Inventory Item
              </DialogTitle>
              <DialogDescription>
                Create a new inventory item with quantity, cost, and market assignment
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Item Name */}
              <div className="space-y-2">
                <Label htmlFor="add-name">Item Name *</Label>
                <Input
                  id="add-name"
                  value={addFormData.name}
                  onChange={(e) => handleAddFormChange('name', e.target.value)}
                  placeholder="e.g., Base Coating - White"
                />
              </div>

              {/* SKU and Market */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="add-sku">SKU *</Label>
                  <Input
                    id="add-sku"
                    value={addFormData.sku}
                    onChange={(e) => handleAddFormChange('sku', e.target.value)}
                    placeholder="e.g., BC-WHT-001"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="add-market">Market *</Label>
                  <Select 
                    value={addFormData.market}
                    onValueChange={(value) => handleAddFormChange('market', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {getInventoryMarkets().map((market) => (
                        <SelectItem key={market} value={market}>
                          {market}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label htmlFor="add-category">Category *</Label>
                <Select 
                  value={addFormData.category}
                  onValueChange={(value) => handleAddFormChange('category', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Base Coatings">Base Coatings</SelectItem>
                    <SelectItem value="Pigments">Pigments</SelectItem>
                    <SelectItem value="Primers">Primers</SelectItem>
                    <SelectItem value="Sealers">Sealers</SelectItem>
                    <SelectItem value="Additives">Additives</SelectItem>
                    <SelectItem value="Supplies">Supplies</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Quantity and Unit */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="add-quantity">Quantity *</Label>
                  <Input
                    id="add-quantity"
                    type="number"
                    value={addFormData.quantity}
                    onChange={(e) => handleAddFormChange('quantity', parseFloat(e.target.value) || 0)}
                    placeholder="450"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="add-unit">Unit *</Label>
                  <Select 
                    value={addFormData.unit}
                    onValueChange={(value) => handleAddFormChange('unit', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gallons">Gallons</SelectItem>
                      <SelectItem value="rolls">Rolls</SelectItem>
                      <SelectItem value="units">Units</SelectItem>
                      <SelectItem value="boxes">Boxes</SelectItem>
                      <SelectItem value="pounds">Pounds</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Min Stock and Cost Per Unit */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="add-minStock">Minimum Stock *</Label>
                  <Input
                    id="add-minStock"
                    type="number"
                    value={addFormData.minStock}
                    onChange={(e) => handleAddFormChange('minStock', parseFloat(e.target.value) || 0)}
                    placeholder="200"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="add-costPerUnit">Cost Per Unit ($) *</Label>
                  <Input
                    id="add-costPerUnit"
                    type="number"
                    step="0.01"
                    value={addFormData.costPerUnit}
                    onChange={(e) => handleAddFormChange('costPerUnit', parseFloat(e.target.value) || 0)}
                    placeholder="45.50"
                  />
                </div>
              </div>

              {/* Last Restocked */}
              <div className="space-y-2">
                <Label htmlFor="add-lastRestocked">Last Restocked *</Label>
                <Input
                  id="add-lastRestocked"
                  type="date"
                  value={addFormData.lastRestocked}
                  onChange={(e) => handleAddFormChange('lastRestocked', e.target.value)}
                />
              </div>

              {/* Preview Calculated Values */}
              <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Stock Status</p>
                    <div className="flex items-center gap-2 mt-1">
                      {(() => {
                        const status = getStockStatus(addFormData.quantity, addFormData.minStock);
                        return (
                          <>
                            <div className={`w-2 h-2 rounded-full ${status.color}`}></div>
                            <Badge variant={status.status === 'critical' ? 'destructive' : 'secondary'}>
                              {status.label}
                            </Badge>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Total Value</p>
                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">
                      ${(addFormData.quantity * addFormData.costPerUnit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAddItem}
                disabled={!addFormData.name || !addFormData.sku}
              >
                Add Item
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Formula Dialog */}
        <Dialog open={addFormulaDialogOpen} onOpenChange={setAddFormulaDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Beaker className="h-5 w-5" />
                Create New Coating Formula
              </DialogTitle>
              <DialogDescription>
                Define mixing ratios for a new RhinoShield coating formula
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Formula Name */}
              <div className="space-y-2">
                <Label htmlFor="formula-name">Formula Name *</Label>
                <Input
                  id="formula-name"
                  value={addFormulaData.name}
                  onChange={(e) => handleAddFormulaChange('name', e.target.value)}
                  placeholder="e.g., RhinoShield Custom Gray"
                />
              </div>

              {/* Code and Description */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="formula-code">Formula Code *</Label>
                  <Input
                    id="formula-code"
                    value={addFormulaData.code}
                    onChange={(e) => handleAddFormulaChange('code', e.target.value)}
                    placeholder="e.g., RS-GRY-005"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="formula-description">Description</Label>
                  <Input
                    id="formula-description"
                    value={addFormulaData.description}
                    onChange={(e) => handleAddFormulaChange('description', e.target.value)}
                    placeholder="e.g., Modern gray finish"
                  />
                </div>
              </div>

              {/* Mixing Ratios Header */}
              <div className="pt-2">
                <h4 className="font-semibold text-sm text-gray-900 dark:text-white mb-3">
                  Mixing Ratios (gallons per batch)
                </h4>
                <p className="text-xs text-muted-foreground mb-4">
                  Define the amount of each component needed for one production batch
                </p>
              </div>

              {/* Base Coating and Pigment */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="formula-base">Base Coating (gal) *</Label>
                  <Input
                    id="formula-base"
                    type="number"
                    step="0.5"
                    value={addFormulaData.baseCoating}
                    onChange={(e) => handleAddFormulaChange('baseCoating', parseFloat(e.target.value) || 0)}
                    placeholder="10"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="formula-pigment">Pigment (gal)</Label>
                  <Input
                    id="formula-pigment"
                    type="number"
                    step="0.5"
                    value={addFormulaData.pigment}
                    onChange={(e) => handleAddFormulaChange('pigment', parseFloat(e.target.value) || 0)}
                    placeholder="0 (for white/clear)"
                  />
                </div>
              </div>

              {/* Primer and Sealer */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="formula-primer">Primer (gal) *</Label>
                  <Input
                    id="formula-primer"
                    type="number"
                    step="0.5"
                    value={addFormulaData.primer}
                    onChange={(e) => handleAddFormulaChange('primer', parseFloat(e.target.value) || 0)}
                    placeholder="2"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="formula-sealer">Sealer (gal) *</Label>
                  <Input
                    id="formula-sealer"
                    type="number"
                    step="0.5"
                    value={addFormulaData.sealer}
                    onChange={(e) => handleAddFormulaChange('sealer', parseFloat(e.target.value) || 0)}
                    placeholder="1"
                  />
                </div>
              </div>

              {/* Total Yield Preview */}
              <div className="p-4 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Total Batch Yield</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      Base: {addFormulaData.baseCoating} + Pigment: {addFormulaData.pigment} + Primer: {addFormulaData.primer} + Sealer: {addFormulaData.sealer}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                      {(addFormulaData.baseCoating + addFormulaData.pigment + addFormulaData.primer + addFormulaData.sealer).toFixed(1)}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">gallons</p>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setAddFormulaDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAddFormula}
                disabled={!addFormulaData.name || !addFormulaData.code}
              >
                <Beaker className="h-4 w-4 mr-2" />
                Create Formula
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
